import React, { useEffect } from 'react';
import moment from 'moment';
import { useStore } from '../../../../../stores/root';

// assets

// services
import GeneralService from '../../../../../services/general';
import PermissionsService from '../../../../../services/permissions';

// props
import { IOrderPriceUpdateResourceShortProps } from '../../../../../props/orders/priceUpdates';

// components
import { CommandBar, DefaultButton, ICommandBarItemProps, MessageBarType, Panel, PanelType, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import Label from '../../../../typography/label';
import Text from '../../../../typography/text';
import Details from '../../../../typography/details';
import OrderPriceUpdateService from '../../../../../services/orders/priceUpdates';
import SimpleMessageBarComponent from '../../../../feedbacks/simpleMessageBar';
import ApproveOrderPriceUpdateForm from '../form/approve';
import RejectOrderPriceUpdateForm from '../form/reject';

type RequestOrderPriceUpdateDetailsProps = {
    requestId: string;
    onDismissed(refresh?: boolean): void;
}

const RequestOrderPriceUpdateDetails: React.FC<RequestOrderPriceUpdateDetailsProps> = (props: RequestOrderPriceUpdateDetailsProps) => {
    const { banner, user } = useStore();

    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [request, setRequest] = React.useState<IOrderPriceUpdateResourceShortProps | undefined>();
    const [touched, setTouched] = React.useState<boolean>(false);

    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        setLoaded(false);
        const _request = await OrderPriceUpdateService.get(props.requestId);

        setRequest(_request);
        setLoaded(true);
    }

    const renderCommandBar = () => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        if (request) {
            const hasApproveAccess = PermissionsService.hasPermission(['orders.prices.update.approve.all'], user.permissions);
            const hasRejectAccess = PermissionsService.hasPermission(['orders.prices.update.reject.all'], user.permissions);
            const isSales = (request.order.sales || []).findIndex((s) => s.sales.id === user.data?.id) > -1;

            // only people with approve all price update and sales can approve
            if ((hasApproveAccess || isSales) && request.status === 'requested') {
                items.push({
                    key: 'approve',
                    text: 'Approve',
                    iconProps: {iconName: "CheckMark"},
                    onClick: () => {
                        setActiveSurface('request.approve');
                    }
                });
            }

            // only people with reject all price update and sales can reject
            if ((hasRejectAccess || isSales) && request.status === 'requested') {
                items.push({
                    key: 'reject',
                    text: 'Reject',
                    iconProps: {iconName: "Xmark"},
                    onClick: () => {
                        setActiveSurface('request.reject');
                    }
                });
            }
        }

        if (items.length > 0 || farItems.length > 0) {
            return <CommandBar items={items} farItems={farItems} styles={{root: {padding: 0, margin: 0}}} />
        } else {
            return null;
        }
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        if (refresh) {
            setTouched(true);
            init();
        }
    }

    return <Panel headerText={'Price Update Request Details'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(touched)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(touched) }} />
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Retrieving details ..."} /></Stack> : null}
            {
                loaded && request ? <>
                    {renderCommandBar()}
                    {request.status ===  'approved' ? <SimpleMessageBarComponent properties={{
                        type: MessageBarType.success,
                        text: `This request has been approved by ${request.respondedBy?.name} at ${moment(request.respondedAt).format("DD/MM/YYYY")}. Justification: ${request.justification || ""}`
                    }} /> : null}
                    {request.status ===  'rejected' ? <SimpleMessageBarComponent properties={{
                        type: MessageBarType.error,
                        text: `This request has been rejected by ${request.respondedBy?.name} at ${moment(request.respondedAt).format("DD/MM/YYYY")}. Justification: ${request.justification || ""}`
                    }} /> : null}
                    {request.container ? <>
                        <Label>Container Details</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Container number' text={request.container.containerNumber || "-"} style={{ width: '50%' }} />
                            <Details title='Size' text={request.container.size || "-"} style={{ width: '50%' }} />
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Seal number' text={request.container.sealNumber || "-"} style={{ width: '50%' }} />
                            <Details title='RO number' text={request.container.roNumber || "-"} style={{ width: '50%' }} />
                        </Stack>
                    </> : null}
                    {request.truck ? <>
                        <Label>Truck Details</Label>
                        <Details title='Registration number' text={request.truck.vehicleRegistrationNumber || "-"} />
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Pick-up address' text={request.truck.originAddress || "-"} style={{ width: '50%' }} />
                            <Details title='Drop-off address' text={request.truck.destinationAddress || "-"} style={{ width: '50%' }} />
                        </Stack>
                    </> : null}
                    {request.goods ? <>
                        <Label>Truck Details</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Name' text={request.goods.goods.name || "-"} style={{ width: '50%' }} />
                            <Details title='Quantity' text={request.goods.quantityAmount + " " + (request.goods.goods.quantityUnit || "")} style={{ width: '50%' }} />
                        </Stack>
                        <Details title='Notes' text={request.goods.notes || "-"} />
                    </> : null}
                    <Stack className={'divider'}></Stack>
                    <Label size={'small'}>Pricing Details</Label>
                    <Details title='Pricing method' text={request.pricingMethod} />
                    <Stack>
                        <Details title={'Price'} text={`Rp. ${GeneralService.getNumberWithSeparator(request.price || "0")}`} />
                        <Stack horizontal tokens={{childrenGap: 5}}>
                            {request.includePPn ? <Text size={'small'} className={'color-green'}>(include PPn)</Text> : null}
                            {request.includePPh ? <Text size={'small'} className={'color-green'}>(include PPh)</Text> : null}
                            {!request.includePPn ? <Text size={'small'} className={'color-red'}>(exclude PPn)</Text> : null}
                            {!request.includePPh ? <Text size={'small'} className={'color-red'}>(exclude PPh)</Text> : null}
                        </Stack>
                    </Stack>
                    <Stack horizontal tokens={{childrenGap: 20}}>
                        <Details title={`PPn (${request.ppnPercentage}%)`} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(request.ppn))}`} style={{width: '50%'}} />
                        <Details title={`PPh (${request.pphPercentage}%)`} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(request.pph))}`} style={{width: '50%'}} />
                    </Stack>
                    <Stack horizontal tokens={{childrenGap: 20}}>
                        <Details title={`Price (inc. PPn & PPh)`} text={`Rp. ${GeneralService.getNumberWithSeparator(request.totalPrice)}`} style={{width: '50%'}} />
                        <Details title={`Invoice price (total price - PPh)`} text={`Rp. ${GeneralService.getNumberWithSeparator(request.invoicePrice)}`} style={{width: '50%'}} />
                    </Stack>
                </> : null
            }
        </Stack>
        {activeSurface === 'request.approve' ? <ApproveOrderPriceUpdateForm requestId={props.requestId} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'request.reject' ? <RejectOrderPriceUpdateForm requestId={props.requestId} onDismissed={_onSurfaceDismissed} /> : null}
    </Panel >
};

export default RequestOrderPriceUpdateDetails;
